import store from "../../../../../../../store";
import {CabinetsSet, CountertopPositions, WallPositions} from "../../../../../helpers/sqftCabinetsDataMapper";
import CabinetLoader from "./modelsLoader/CabinetLoaderV2";
import {CABINET_BATHROOM_LABELS, CABINET_PART_IDS, SQFT_VIEW_MODE} from "../../../../../constants";
import CountertopLoader from "./modelsLoader/CountertopLoader";
import OverlayNo3dNotifier from "./overlay/OverlayNo3dNotifier";
import {computeSceneMetrics} from "./helpers/sceneMetrics";
import TaskRunner from "./helpers/TaskRunner";
import {addShadowsForAllModels} from "../../../../../helpers/sqft3dArSceneHelper";
import WallsLoader from "./modelsLoader/WallsLoader";

class SceneModelsLoader {
  constructor(sceneViewer, scene, modelScale) {
    this.sceneViewer = sceneViewer;
    this.scene = scene;
    this.modelScale = modelScale;
    this.loader = new window.GLTFLoader();
    this.loadedInstances = [];
    this.modelsCenter = null;
    this.modelsSize = null;
    this.notifier = new OverlayNo3dNotifier(sceneViewer);

    this.loaders = {
      cabinets: {
        fetch: async () => CabinetsSet.prepareCabinetsPositions(
          store.getState().sqftState.SQFTViewMode === SQFT_VIEW_MODE.threeDModeFramed
        ),
        loader: new CabinetLoader({ scene, loader: this.loader, modelScale, typeKey: 'cabinets' })
      },
      countertops: {
        fetch: CountertopPositions.prepareCountertopPositions,
        loader: new CountertopLoader({ scene, loader: this.loader, modelScale, typeKey: 'countertops' })
      },
      walls: {
        fetch: WallPositions.prepareWallPositions,
        loader: new WallsLoader({ scene, loader: this.loader, modelScale, typeKey: 'walls' })
      }
    };
  }

  async loadAllModels() {
    const isMobile = store.getState().serverState.isMobile
      || /iPad|iPhone/.test(navigator.userAgent);

    this.loadedInstances = [];

    const tasks = Object.values(this.loaders).map(item => async () => {
      const positions = await item.fetch();
      console.log("positions", positions);
      await this._loadPositions(item.loader, positions);
    });

    if (isMobile) {
      await TaskRunner.sequential(tasks);
    } else {
      await TaskRunner.parallel(tasks);
    }

    //log all material maps QCDES-6966
    const meshTextures = {};
    this.scene.traverse(obj => {
      if (obj.isMesh) {
        const mat = obj.material;
        meshTextures[obj.name || obj.uuid] = {
          map: mat.map ? mat.map.uuid : null,
          normalMap: mat.normalMap ? mat.normalMap.uuid : null,
          roughnessMap: mat.roughnessMap ? mat.roughnessMap.uuid : null,
          metalnessMap: mat.metalnessMap ? mat.metalnessMap.uuid : null,
        };
      }
    });
    //console.log(JSON.stringify(meshTextures, null, 2));

    this._updateMetrics();
    this._checkEmptyScene();
    this.sceneViewer.modelsLoaded = true;
  }

  async reloadModels(typeKey, positions) {
    const entry = this.loaders[typeKey];
    if (!entry) {
      console.log(`Unknown loader type "${typeKey}"`);
      return null
    }
    const { loader } = entry;

    // 1) remove old models
    positions.forEach(data => loader.remove(data));

    // 2) clear loadedInstances
    this.loadedInstances = this.loadedInstances.filter(inst =>
      inst.userData._type !== typeKey ||
      !positions.some(d => loader._makeKey(d) === inst.userData._key)
    );

    // 3) load new
    const fresh = await Promise.all(positions.map(d => loader.load(d)));
    fresh.filter(Boolean).forEach(inst => this.loadedInstances.push(inst));

    // 4) recalculate center
    this._updateMetrics();

    // 4) hide no 3d message
    this._checkEmptyScene();

    addShadowsForAllModels(this.scene);

    return fresh;
  }

  removeAllModels(typeKey) {
    const entry = this.loaders[typeKey];
    if (!entry) throw new Error(`Unknown loader type "${typeKey}"`);
    const { loader } = entry;

    const toRemove = this.loadedInstances.filter(inst => inst.userData._type === typeKey);
    toRemove.forEach(inst => loader.remove(inst.userData));
    this.loadedInstances = this.loadedInstances.filter(inst => inst.userData._type !== typeKey);
    this._updateMetrics();
    this._checkEmptyScene();
  }

  async _loadPositions(loader, positions) {
    const tasks = positions.map(data => async () => {
      const inst = await loader.load(data);
      if (inst) this.loadedInstances.push(inst);
    });
    const isMobile = store.getState().serverState.isMobile
      || /iPad|iPhone/.test(navigator.userAgent);

    if (isMobile) {
      await TaskRunner.sequential(tasks);
    } else {
      await TaskRunner.parallel(tasks);
    }
  }

  _updateMetrics() {
    const { center, size } = computeSceneMetrics(this.loadedInstances);
    this.modelsCenter = center;
    this.modelsSize   = size;
  }

  _checkEmptyScene() {
    const count = this.loadedInstances.filter(i => [...CABINET_PART_IDS, ...Object.keys(CABINET_BATHROOM_LABELS)].includes(i.userData.partId)).length;
    if (!count) {
      this.notifier.showNo3D();
    } else {
      this.notifier.hide();
      if (!this.countertopsLoaded) {
        this._loadCountertopsOnce().then(r => void r);
        this.sceneViewer.calculateAndSetCameraPosition(
          this.sceneViewer.modelsLoader.modelsCenter,
          this.sceneViewer.modelsLoader.modelsSize
        );
      }
    }
  }

  async _loadCountertopsOnce() {
    const entry = this.loaders['countertops'];
    const positions = await entry.fetch();
    await this._loadPositions(entry.loader, positions);
    this.countertopsLoaded = true;
    this._updateMetrics();
  }
}

export default SceneModelsLoader;
